import './captureInviteHash';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Types
export interface ImagePrompt {
  prompt: string;
  alt_text: string;
  placement: string;
}

// Script Framework Engine validation report (migration 009 shape)
export type ValidationStatus = 'pass' | 'warn' | 'error';
export type ValidationSeverity = 'error' | 'warning' | 'info';

export interface ValidationIssue {
  severity: ValidationSeverity;
  message: string;
  location?: string | null;
  code?: string | null;
}

export interface ValidatorResult {
  name: string;
  status: ValidationStatus;
  issues: ValidationIssue[];
}

export interface ValidationReport {
  framework_id: string;
  framework_version?: string;
  overall_status: ValidationStatus;
  error_count: number;
  warning_count: number;
  validators: ValidatorResult[];
  generated_at: string;
}

// Short-Form Content (Phase 3.1) — migration 011 shape
// See PRDs/phase_3_1_sfc_v1_prd.md §4.4
export interface ShortFormValidationIssue {
  severity: ValidationSeverity;
  message: string;
  code?: string | null;
  location?: string | null;
}

export interface ShortFormValidation {
  ban_list_status: ValidationStatus;
  duration_status: ValidationStatus;
  warnings: ShortFormValidationIssue[];
  errors: ShortFormValidationIssue[];
}

export interface ShortFormScript {
  id: string;
  variant: string;
  clip_label?: string | null;
  strategy?: string | null;
  target_duration_seconds?: number | null;
  platforms_compatible: string[];
  duration_estimate_seconds: number;
  word_count: number;
  hook: string;
  body_markdown: string;
  cta: string;
  validation: ShortFormValidation;
  framework_id: string;
  generated_at: string;
}

export interface ShortFormScriptsPayload {
  version: 1 | 2;
  scripts: ShortFormScript[];
}

export interface Draft {
  id: string;
  job_id: string;
  title: string;
  content_html: string | null;
  content_markdown: string | null;
  meta_description: string | null;
  focus_keyword: string | null;
  secondary_keywords: string[] | null;
  status: 'draft' | 'pending_review' | 'approved' | 'rejected' | 'published';
  wordpress_post_id: number | null;
  wordpress_url: string | null;
  site_identifier: string | null;
  video_id?: string;
  youtube_url?: string;
  script_id?: string;
  reviewer_notes: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  image_prompts: ImagePrompt[] | null;
  // Unified content engine fields
  quality_score: number | null;
  input_type: string | null;
  site_id: string | null;
  schema_markup: Record<string, unknown> | null;
  youtube_script: string | null;
  youtube_metadata: Record<string, unknown> | null;
  image_errors: { error: string; failed_count: number } | null;
  regenerating_image: number | null;
  // Script Framework Engine output (Phase 2 — Tasks 17-23)
  validation_report: ValidationReport | null;
  framework_id: string | null;
  framework_version: string | null;
  // Short-Form Content (Phase 3.1) — universal 60s script per run
  short_form_scripts: ShortFormScriptsPayload | null;
}

export interface BlogPost {
  id: string;
  wordpress_post_id: number;
  wordpress_url: string;
  title: string;
  status: string;
  created_at: string;
}

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseKey);

// API Functions
export async function fetchPendingDrafts(siteId?: string): Promise<Draft[]> {
  try {
    let query = supabase
      .from('blog_posts')
      .select(`
        *,
        jobs:job_id (
          video_id,
          youtube_url,
          input_type,
          site_id
        )
      `)
      .eq('status', 'pending_review');

    if (siteId) {
      query = query.eq('site_id', siteId);
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.warn('Join query failed, falling back to simple query:', error.message);
      throw error;
    }

    const drafts = (data || []).map((item: Record<string, unknown>) => {
      const job = item.jobs as { video_id?: string; youtube_url?: string; input_type?: string; site_id?: string } | null;
      return {
        ...item,
        video_id: job?.video_id || (item.video_id as string | undefined),
        youtube_url: job?.youtube_url,
        jobs: undefined,
      };
    });

    return drafts as Draft[];
  } catch {
    let fallback = supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'pending_review');

    if (siteId) {
      fallback = fallback.eq('site_id', siteId);
    }

    const { data, error } = await fallback
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) throw error;
    return (data as Draft[]) || [];
  }
}

export async function fetchDraftById(draftId: string): Promise<Draft> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', draftId)
    .single();

  if (error) throw error;
  return data as Draft;
}

export async function fetchRecentPosts(limit: number = 10): Promise<BlogPost[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, wordpress_post_id, wordpress_url, title, status, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data as BlogPost[]) || [];
}
